import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";

// CRAG (Corrective RAG) grades the retrieved+augmented pool. A broader corrective
// search only fires when the pool is weak/ambiguous — escalation is GATED, so a
// strong pool passes straight through. For this query the verdict is CORRECT.
interface Verdict {
  key: string;
  desc: string;
  action: string;
  color: string;
  chosen: boolean;
  delay: number;
}

const VERDICTS: Verdict[] = [
  { key: "CORRECT", desc: "pool answers the query", action: "use as-is → proceed", color: COLORS.accentGreen, chosen: true, delay: 70 },
  { key: "AMBIGUOUS", desc: "partial / mixed signal", action: "broaden + refine", color: COLORS.accentYellow, chosen: false, delay: 85 },
  { key: "INCORRECT", desc: "pool misses the query", action: "corrective broader search", color: COLORS.accentRed, chosen: false, delay: 100 },
];

export const SceneCrag: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });
  const exitOpacity = interpolate(frame, [210, 240], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const graderAppear = spring({ frame: frame - 15, fps, config: SPRING.bouncy });
  const isGrading = frame > 20 && frame < 70;

  // Qualitative confidence meter (visual only — no asserted numeric score).
  const conf = interpolate(
    spring({ frame: frame - 120, fps, config: SPRING.gentle }),
    [0, 1],
    [0, 0.9],
  );

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.06} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 50,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
        }}
      >
        <div style={{ fontSize: 42, fontFamily: FONTS.heading, fontWeight: 700, color: COLORS.textPrimary }}>
          Step 6:{" "}
          <span style={{ color: COLORS.accentYellow }}>CRAG Quality Gate</span>
        </div>
        <div style={{ fontSize: 18, fontFamily: FONTS.body, color: COLORS.textSecondary, marginTop: 8 }}>
          Grade the retrieved pool — a broader corrective search fires only when it&rsquo;s weak
        </div>
      </div>

      {/* Left: grader node reading the pool */}
      <div
        style={{
          position: "absolute",
          left: 210,
          top: 470,
          transform: `translate(-50%, -50%) scale(${interpolate(graderAppear, [0, 1], [0.3, 1])})`,
          opacity: interpolate(graderAppear, [0, 1], [0, 1]),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 108,
            height: 108,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${COLORS.accentYellow}25, ${COLORS.accentOrange}12)`,
            border: `2px solid ${COLORS.accentYellow}50`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 46,
            boxShadow: isGrading ? `0 0 34px ${COLORS.accentYellow}30` : "none",
          }}
        >
          🧪
        </div>
        <span style={{ fontFamily: FONTS.mono, fontSize: 13, color: isGrading ? COLORS.accentYellow : COLORS.textMuted }}>
          {isGrading ? "grading pool…" : "CRAG grader"}
        </span>
        <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: COLORS.textMuted }}>
          ~158 chunks in
        </span>
      </div>

      {/* Center: three verdict lanes */}
      <div style={{ position: "absolute", left: 470, top: 250, width: 700 }}>
        {VERDICTS.map((v, i) => {
          const rowAppear = spring({ frame: frame - v.delay, fps, config: SPRING.snappy });
          const dim = frame > 140 && !v.chosen;
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                padding: "16px 22px",
                marginBottom: 14,
                borderRadius: 12,
                background: v.chosen ? `${v.color}12` : `${COLORS.bgCard}`,
                border: `1.5px solid ${v.chosen ? v.color : COLORS.border}`,
                boxShadow: v.chosen && frame > 140 ? `0 0 26px ${v.color}25` : "none",
                opacity: interpolate(rowAppear, [0, 1], [0, dim ? 0.32 : 1]),
                transform: `translateX(${interpolate(rowAppear, [0, 1], [24, 0])}px)`,
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 18,
                  fontWeight: 700,
                  color: v.color,
                  minWidth: 130,
                }}
              >
                {v.key}
              </span>
              <span style={{ fontFamily: FONTS.body, fontSize: 15, color: COLORS.textSecondary, flex: 1 }}>
                {v.desc}
              </span>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  color: v.color,
                  padding: "4px 12px",
                  borderRadius: 6,
                  background: `${v.color}15`,
                  whiteSpace: "nowrap",
                }}
              >
                {v.action}
              </span>
              {v.chosen && frame > 140 && (
                <span style={{ fontSize: 18 }}>←</span>
              )}
            </div>
          );
        })}

        {/* Confidence meter */}
        <div style={{ marginTop: 10, opacity: interpolate(spring({ frame: frame - 118, fps, config: SPRING.snappy }), [0, 1], [0, 1]) }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontFamily: FONTS.mono,
              fontSize: 12,
              color: COLORS.textMuted,
              marginBottom: 6,
            }}
          >
            <span>retrieval quality</span>
            <span style={{ color: COLORS.accentGreen }}>strong → pass</span>
          </div>
          <div style={{ height: 10, borderRadius: 5, background: `${COLORS.accentGreen}12`, overflow: "hidden" }}>
            <div
              style={{
                width: `${conf * 100}%`,
                height: "100%",
                borderRadius: 5,
                background: `linear-gradient(90deg, ${COLORS.accentYellow}, ${COLORS.accentGreen})`,
                boxShadow: `0 0 10px ${COLORS.accentGreen}40`,
              }}
            />
          </div>
        </div>
      </div>

      {/* Bottom note */}
      {(() => {
        const noteAppear = spring({ frame: frame - 160, fps, config: SPRING.snappy });
        return (
          <div
            style={{
              position: "absolute",
              bottom: 70,
              left: "50%",
              transform: "translate(-50%, 0)",
              opacity: interpolate(noteAppear, [0, 1], [0, 1]),
              fontFamily: FONTS.mono,
              fontSize: 15,
              color: COLORS.textMuted,
              textAlign: "center",
            }}
          >
            Escalation is <span style={{ color: COLORS.accentGreen, fontWeight: 700 }}>gated</span> — a strong pool skips the
            corrective search (no wasted latency)
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
